import { css } from '@emotion/react'
import styles, { colors, typo } from '@mathflat/common/style'

const {
  colors: { gray, red, primary },
} = styles

const dotStyle = css`
  display: inline-block;
  margin-right: 4px;
  content: '';
  width: 4px;
  height: 4px;
  border-radius: var(--Radius-Circle);
`

const statusStyle = css`
  position: relative;
  background-color: white;
  border-radius: var(--Radius-Circle);
  width: 55px;
`

const S = {
  basic: css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 40px;
    border-radius: var(--Radius-100);

    transition: opacity 0.4s cubic-bezier(0.25, 1, 0.5, 1);

    &:disabled {
      cursor: not-allowed;
    }

    &.loading {
      cursor: wait;
      opacity: 0.7;
    }

    &.disabled-like,
    &.disabled-like:hover {
      border: 1px solid ${gray.$300};
      background-color: ${colors.gray.$200} !important;
      color: ${colors.gray.$600};
    }

    &.disabled-like:hover {
      cursor: default;
    }
  `,
  theme: {
    none: css`
      background-color: white;
      border: 1px solid ${gray.$400};
      :disabled {
        border: 1px solid ${gray.$200};
        color: ${gray.$600};
      }
    `,
    default: css`
      background-color: ${gray.$200};
      color: ${gray.$800};
      :disabled {
        background-color: ${gray.$200};
        color: ${gray.$500};
      }
      &:not(.loading, :disabled):hover {
        background-color: ${gray.$300};
      }
    `,
    transparent: css`
      background-color: transparent;
    `,
    individual: css`
      background-color: ${gray.$100};
      border: 1px solid ${gray.$400};
      color: ${gray.$900};

      :disabled {
        background-color: ${gray.$100};
        border-color: ${gray.$230};
        color: ${gray.$500};
      }

      &:not(.loading, :disabled):hover {
        background-color: ${gray.$200};
      }
    `,
    attendance: css`
      border: 1px solid ${gray.$400};
      background-color: ${gray.$100};
      color: ${gray.$900};

      :disabled {
        background-color: ${gray.$100};
        border-color: ${gray.$230};
        color: ${gray.$500};
      }

      &:not(.loading, :disabled):hover {
        border: 1px solid ${gray.$500};
        background-color: ${gray.$200};
      }
    `,
    icon: css`
      border: 1px solid ${gray.$300};
      background-color: white;
      color: ${gray.$600};

      :disabled {
        background-color: ${gray.$100};
        color: ${gray.$400};
      }
    `,
    primary: css`
      background-color: ${primary};
      color: white;
      font-weight: 700;

      &:not(.loading):disabled {
        background-color: ${gray.$200};
        color: ${gray.$500};
      }

      &:not(.loading, :disabled):hover {
        background-color: var(--Colors-Green-600);
      }
    `,
    'dark-theme': css`
      background-color: ${gray.$800};
      color: white;
    `,
    'individual-white-bg': css`
      background-color: white;
      border: 1px solid ${gray.$500};
      color: ${gray.$800};

      :disabled {
        background-color: ${gray.$100};
        border-color: ${gray.$400};
        color: ${gray.$500};
      }
    `,
    'individual-wrong': css`
      background-color: ${red.$100};
      border: 1px solid ${red.$200};
      color: ${red.$300};

      :disabled {
        background-color: ${gray.$100};
        border-color: ${gray.$300};
        color: ${gray.$500};
      }

      &:not(.loading, :disabled):hover,
      &:not(.loading, :disabled):focus {
        background-color: ${red.$230};
      }
    `,

    'individual-highlight': css`
      background-color: ${gray.$100};
      border: 1px solid ${primary};
      color: ${primary};

      :disabled {
        background-color: ${gray.$100};
        border-color: ${gray.$230};
        color: ${gray.$500};
      }

      :disabled {
        background-color: ${gray.$100};
        color: ${gray.$500};
      }

      &:not(.loading, :disabled):hover,
      &:not(.loading, :disabled):focus {
        background-color: var(--Colors-Green-50);
      }
    `,
    'individual-inactive': css`
      ${typo.caption01};
      background: ${gray.$200};
      color: ${gray.$600};
    `,
    'individual-gray': css`
      background-color: ${gray.$100};
      color: ${gray.$900};
      border: 1px solid ${gray.$400};
    `,
    'individual-select': css`
      background-color: var(--Colors-Green-50);
      border: 1px solid var(--Colors-Green-200);
      color: var(--Colors-Green-500);

      &:disabled {
        background-color: ${gray.$100};
        border-color: ${gray.$300};
        color: ${gray.$500};
      }
    `,
    'individual-default': css`
      background-color: ${gray.$100};
      color: ${gray.$900};
    `,
    'individual-answer': css`
      color: ${gray.$900};
      background-color: ${gray.$100};
      border: 1px solid ${gray.$400};
      &:not(.loading, :disabled):hover,
      &:not(.loading, :disabled):focus {
        background-color: ${gray.$200};
      }
    `,
    'primary-caution': css`
      background-color: ${red.$300};
      color: white;
      font-weight: 700;
      :disabled {
        background-color: ${gray.$200};
        color: ${gray.$500};
      }
      &:not(.loading, :disabled):hover,
      &:not(.loading, :disabled):focus {
        background-color: #ff5c6b;
      }
    `,
    'primary-dark': css`
      background-color: ${gray.$900};
      color: white;
      font-weight: 700;

      :disabled {
        background-color: ${gray.$200};
        color: ${gray.$500};
      }
    `,
    'text-button': css`
      background-color: white;
      color: ${gray.$700};
      border: 1px solid ${gray.$400};
    `,
    'check-blue': css`
      ${typo.caption01};
      background-color: ${gray.$100};
      color: var(--Colors-Green-500);
    `,
    'check-gray': css`
      ${typo.caption01};
      background-color: ${gray.$100};
      color: ${gray.$500};
    `,
    'group-select': css`
      ${typo.body02};
      color: ${gray.$800};
      font-weight: 700;
      background-color: ${gray.$100};
    `,
    'status-enter': css`
      ${statusStyle};
      border: 1px solid ${primary};
      color: ${primary};
      ${typo.caption01};

      ::before {
        ${dotStyle};
        background-color: ${primary};
      }
    `,
    'status-quit': css`
      ${statusStyle};
      border: 1px solid ${gray.$900};
      color: ${gray.$900};
      ${typo.caption01};

      ::before {
        ${dotStyle};
        background-color: ${gray.$900};
      }
    `,
    'status-출석': css`
      ${statusStyle};
      border: 1px solid ${gray.$400};
      color: ${gray.$700};
      ${typo.caption01};
      :hover {
        border: 1px solid ${gray.$500};
      }
    `,
  },
}

export default S
